import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { Hotel, Ticket, Globe, FileText, Briefcase, Flower2, CheckCircle2, Sparkles, Sun, Activity, Heart, Trees, Camera } from 'lucide-react';
import passportPattern from '../assets/passport_pattern.webp';
import majesticIndia from '../assets/majestic_india_hero.webp';
import wellnessImg from '../assets/ayurveda_wellness.webp';

const services = [
  { title: "Heritage Hotels", description: "Stay in living palaces and luxury retreats.", icon: Hotel, color: "bg-orange-600" },
  { title: "Seamless Flights", description: "Premium domestic and international travel.", icon: Ticket, color: "bg-amber-600" },
  { title: "Bespoke Packages", description: "Curated journeys across Bharat.", icon: Globe, color: "bg-emerald-700" },
  { title: "Yoga & Wellness", description: "Spiritual retreats and Ayurvedic healing.", icon: Flower2, color: "bg-rose-600" },
  { title: "Quick Visas", description: "Hassle-free documentation services.", icon: FileText, color: "bg-indigo-600" },
  { title: "Corporate Travel", description: "Professional business travel solutions.", icon: Briefcase, color: "bg-slate-800" }
];

const yogaBenefits = [
  { title: "Physical Vitality", desc: "Enhance flexibility, strength, and posture through traditional Asanas." },
  { title: "Mental Clarity", desc: "Reduce stress and improve focus with ancient meditation techniques." },
  { title: "Inner Balance", desc: "Align your Chakras and find harmony between mind, body, and soul." },
  { title: "Spiritual Growth", desc: "Experience the profound peace of the land where Yoga was born." }
];

const healthcareBenefits = [
  { title: "Expert Doctors", desc: "Consult with world-renowned specialists and surgeons across various disciplines." },
  { title: "Affordable Treatment", desc: "Access high-quality medical procedures and care at a fraction of international costs." },
  { title: "International Hospitals", desc: "Experience state-of-the-art facilities with global accreditations like JCI and NABH." },
  { title: "Recovery & Wellness", desc: "Enjoy holistic post-treatment care, combining advanced medicine with traditional therapies." }
];

const wildlifeBenefits = [
  { title: "Exotic Safaris", desc: "Embark on thrilling open-jeep safaris through dense jungles." },
  { title: "Tiger Spotting", desc: "Witness the majestic Bengal Tiger in its natural habitat." },
  { title: "Bird Watching", desc: "Discover hundreds of rare and endemic avian species." },
  { title: "Eco-Lodges", desc: "Stay in sustainable, luxury accommodations right in the wild." }
];

const yogaPoseImg = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=1000";
const medicalImg1 = "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&q=80&w=1000";
const medicalImg2 = "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=1000";
const elephantImg = "https://upload.wikimedia.org/wikipedia/commons/9/98/Elephas_maximus_%28Bandipur%29.jpg";
const natureImg = "https://images.pexels.com/photos/1179229/pexels-photo-1179229.jpeg?auto=compress&cs=tinysrgb&w=2000";
const indianNatureHero = "https://images.pexels.com/photos/13691355/pexels-photo-13691355.jpeg?auto=compress&cs=tinysrgb&w=2000";

const Services = () => {
  return (
    <div className="bg-[#FFFBF5] min-h-screen relative overflow-x-hidden">
      <SEO 
        title="Bespoke Travel Services" 
        description="Discover our range of premium travel services: custom tour packages, corporate retreats, flight bookings, visa assistance, and 24/7 dedicated support with Sunshine Travels." 
        canonicalPath="/services" 
      />
      {/* Indian Motif Watermark */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url(${passportPattern})`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }}
      ></div>

      {/* Hero Section - Indian Nature */}
      <section className="relative h-[calc(65vh-1.5cm)] flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={indianNatureHero}
            alt="Kerala Backwaters"
            className="w-full h-full object-cover object-center scale-105"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF5] via-transparent to-black/20"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="space-y-6 max-w-3xl"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-[1px] bg-orange-500"></div>
              <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] block">
                Atithi Devo Bhava
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white leading-snug tracking-wide">
              Our <span className="text-orange-500 italic">Signature</span> <br />
              Services
            </h1>
            <p className="text-white/90 text-lg md:text-xl font-medium leading-relaxed">
              Experience the soul of India through our meticulously crafted travel solutions, where every detail is a tribute to heritage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-xs font-black uppercase text-orange-600 tracking-[0.4em]">What We Offer</h2>
          <p className="text-3xl md:text-4xl font-serif font-bold text-slate-900 italic">Curated with Passion, Delivered with Care</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group relative bg-white p-8 rounded-[3rem] border border-orange-100 shadow-[0_20px_50px_-20px_rgba(255,153,51,0.1)] hover:shadow-[0_30px_60px_-15px_rgba(255,153,51,0.2)] transition-all duration-500"
            >
              <div className={`w-14 h-14 ${service.color} text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg transform group-hover:rotate-12 transition-transform duration-500`}>
                <service.icon size={28} />
              </div>
              <h3 className="text-xl font-serif font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">{service.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6 font-medium">{service.description}</p>
              <div className="h-[2px] w-0 bg-orange-500 group-hover:w-full transition-all duration-700"></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Yoga & Wellness Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-5 pointer-events-none">
          <Flower2 size={600} className="text-orange-500 -mr-40 mt-20" />
        </div>

        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-orange-100/50 rounded-[4rem] -rotate-3"></div>

              {/* Image Stack - Focused on Yoga */}
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src={wellnessImg}
                    alt="Ayurveda Wellness"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="pt-12">
                  <img
                    src={yogaPoseImg}
                    alt="Yoga Pose Silhouette"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover animate-float hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Spiritual Awakening Float */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-slate-900 rounded-[3rem] flex items-center justify-center p-6 text-white shadow-2xl z-20 hidden md:flex">
                <div className="text-center">
                  <Sun size={32} className="mx-auto mb-2 animate-spin-slow text-orange-500" />
                  <span className="text-[8px] font-black uppercase tracking-widest block">Soulful</span>
                  <span className="text-[8px] font-black uppercase tracking-widest block text-orange-500">Awakening</span>
                </div>
              </div>
            </motion.div>

            <div className="space-y-12">
              <div className="space-y-6">
                <div className="flex items-center gap-3 text-orange-600">
                  <Sparkles size={20} />
                  <span className="font-black uppercase tracking-[0.3em] text-[10px]">Holistic Wellness</span>
                </div>
                <h2 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 leading-tight">
                  Rediscover Yourself Through <span className="text-orange-600 italic">Yoga</span>
                </h2>
                <p className="text-slate-600 text-lg font-medium leading-relaxed">
                  India is the birthplace of Yoga and Ayurveda. We invite you to embark on a journey of self-discovery in the serene ashrams of Rishikesh or the tranquil forests of Kerala.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                {yogaBenefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 size={18} />
                      <h4 className="font-bold text-slate-900">{benefit.title}</h4>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{benefit.desc}</p>
                  </motion.div>
                ))}
              </div>

              <motion.a
                href="https://mysticroutes.sunshinemanipal.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-orange-600 text-white px-10 py-5 rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-slate-900 transition-all shadow-xl shadow-orange-600/20"
              >
                Explore Wellness Retreats
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare Section */}
      <section className="py-24 bg-[#FFFBF5] relative overflow-hidden">
        <div className="absolute left-0 top-0 w-1/3 h-full opacity-5 pointer-events-none">
          <Activity size={600} className="text-orange-500 -ml-40 mt-20" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            
            <div className="space-y-12 order-2 lg:order-1">
              <div className="space-y-6">
                <div className="flex items-center gap-3 text-orange-600">
                  <Heart size={20} />
                  <span className="font-black uppercase tracking-[0.3em] text-[10px]">World-Class Healthcare</span>
                </div>
                <h2 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 leading-tight">
                  Heal, Recover & <span className="text-orange-600 italic">Thrive</span> in India
                </h2>
                <p className="text-slate-600 text-lg font-medium leading-relaxed">
                  India is a global destination for advanced medical treatments, affordable healthcare, expert surgeons, and holistic recovery programs. Explore world-class hospitals, specialized treatments, wellness therapies, and personalized medical travel assistance.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                {healthcareBenefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 size={18} />
                      <h4 className="font-bold text-slate-900">{benefit.title}</h4>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{benefit.desc}</p>
                  </motion.div>
                ))}
              </div>

              <motion.a
                href="https://cureconnect.sunshinemanipal.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-orange-600 text-white px-10 py-5 rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-slate-900 transition-all shadow-xl shadow-orange-600/20"
              >
                Explore Medical Tourism
              </motion.a>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative order-1 lg:order-2"
            >
              <div className="absolute -inset-4 bg-orange-100/50 rounded-[4rem] rotate-3"></div>

              {/* Image Stack - Focused on Healthcare */}
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="pt-12">
                  <img
                    src={medicalImg2}
                    alt="Medical Professionals"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover animate-float hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="space-y-4">
                  <img
                    src={medicalImg1}
                    alt="Advanced Medical Facility"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Trust Badge Float */}
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white rounded-[3rem] flex items-center justify-center p-6 text-slate-900 shadow-2xl z-20 hidden md:flex border border-orange-50">
                <div className="text-center">
                  <Activity size={32} className="mx-auto mb-2 animate-pulse text-orange-600" />
                  <span className="text-[8px] font-black uppercase tracking-widest block">Global</span>
                  <span className="text-[8px] font-black uppercase tracking-widest block text-orange-600">Standard</span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Wildlife Heritage Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-5 pointer-events-none">
          <Trees size={600} className="text-orange-500 -mr-40 mt-20" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-orange-100/50 rounded-[4rem] -rotate-3"></div>

              {/* Image Stack - Focused on Wildlife */}
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src={natureImg}
                    alt="Lush Jungle"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="pt-12">
                  <img
                    src={elephantImg}
                    alt="Majestic Elephant"
                    className="rounded-3xl shadow-xl border-4 border-white aspect-[3/4] object-cover animate-float hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Trust Badge Float */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-slate-900 rounded-[3rem] flex items-center justify-center p-6 text-white shadow-2xl z-20 hidden md:flex border border-orange-50">
                <div className="text-center">
                  <Camera size={32} className="mx-auto mb-2 animate-pulse text-orange-500" />
                  <span className="text-[8px] font-black uppercase tracking-widest block">Into The</span>
                  <span className="text-[8px] font-black uppercase tracking-widest block text-orange-500">Wild</span>
                </div>
              </div>
            </motion.div>

            <div className="space-y-12">
              <div className="space-y-6">
                <div className="flex items-center gap-3 text-orange-600">
                  <Trees size={20} />
                  <span className="font-black uppercase tracking-[0.3em] text-[10px]">Natural Heritage</span>
                </div>
                <h2 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 leading-tight">
                  Experience the <br />
                  <span className="text-orange-600 italic">Wild Majesty</span> of Bharat
                </h2>
                <p className="text-slate-600 text-lg font-medium leading-relaxed">
                  From the lush jungles of Kerala to the vast reserves of Central India, encounter the gentle giants and exotic wildlife that define India's vibrant ecosystem.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                {wildlifeBenefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 size={18} />
                      <h4 className="font-bold text-slate-900">{benefit.title}</h4>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{benefit.desc}</p>
                  </motion.div>
                ))}
              </div>

              <motion.a
                href="https://adventure.sunshinemanipal.com/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-orange-600 text-white px-10 py-5 rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-slate-900 transition-all shadow-xl shadow-orange-600/20"
              >
                Explore Wildlife Safaris
              </motion.a>
            </div>

          </div>
        </div>
      </section>

      {/* Signature Process */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img src={majesticIndia} className="w-full h-full object-cover mix-blend-overlay blur-sm" alt="Background" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center max-w-4xl mx-auto mb-20">
          <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] block mb-4">How We Work</span>
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-white leading-tight">Our <span className="text-orange-500 italic">Signature</span> Process</h2>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consult", desc: "Share your travel dreams with us." },
              { step: "02", title: "Curation", desc: "We design your bespoke itinerary." },
              { step: "03", title: "Perfecting", desc: "Refining every detail for you." },
              { step: "04", title: "Journey", desc: "Travel with ultimate peace of mind." }
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                className="relative group text-center space-y-6"
              >
                <div className="w-20 h-20 bg-orange-600 rounded-3xl flex items-center justify-center text-3xl font-serif font-bold text-white mx-auto shadow-2xl group-hover:rotate-12 transition-transform duration-500">
                  {p.step}
                </div>
                <div className="space-y-2">
                  <h4 className="font-black text-xs uppercase tracking-[0.25em] text-white">{p.title}</h4>
                  <p className="text-gray-400 text-xs leading-relaxed">{p.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-full h-[1px] bg-gradient-to-r from-orange-500/50 to-transparent"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Go Section */}
      <section className="py-24 bg-[#FFFBF5] relative overflow-hidden">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="rounded-[3rem] p-8 md:p-12 text-center space-y-6 shadow-2xl relative overflow-hidden group min-h-[350px] flex flex-col justify-center max-w-4xl mx-auto"
          >
            <div className="absolute inset-0 z-0">
              <img
                src={indianNatureHero}
                className="w-full h-full object-cover transition-transform duration-[5s] group-hover:scale-110"
                alt="Nature Background"
              />
            </div>

            <div className="relative z-10 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              <h3 className="text-4xl md:text-6xl font-serif font-bold text-white italic">Ready for an Incredible Journey?</h3>
              <p className="text-white/90 text-lg max-w-2xl mx-auto font-medium mt-6">Join us as we explore the timeless beauty and vibrant culture of the Indian subcontinent.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-10">
                <Link to="/contact" className="bg-orange-600 text-white py-5 px-12 rounded-full font-black uppercase text-xs tracking-[0.3em] hover:bg-white hover:text-orange-600 transition-all shadow-2xl">Book Now</Link>
                <Link to="/destinations" className="text-white font-bold uppercase text-[10px] tracking-[0.4em] border-b-2 border-white/30 pb-2 hover:border-white transition-all">View Destinations</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
